import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import WaveSurfer from 'wavesurfer.js'
import Minimap from 'wavesurfer.js/dist/plugins/minimap.esm.js'
import Regions from 'wavesurfer.js/dist/plugins/regions.esm.js'
import EQControl from './EQControl'
import './Deck.css'

const RAILWAY_API_URL = import.meta.env.VITE_RAILWAY_API_URL || '/api'

const Deck = forwardRef(({ deckData, audioContext, deckLabel }, ref) => {
  const waveformRef = useRef(null)
  const wavesurfer = useRef(null)
  const regionsPlugin = useRef(null)
  const mediaSource = useRef(null)
  const lowFilter = useRef(null)
  const midFilter = useRef(null)
  const highFilter = useRef(null)
  const gainNode = useRef(null)
  const [cuePoint, setCuePoint] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [eq, setEq] = useState({ low: 50, mid: 50, high: 50 })
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [zoomLevel, setZoomLevel] = useState(50) // pixels per second
  const [isScrubbing, setIsScrubbing] = useState(false)
  const wasPlayingBeforeScrub = useRef(false)
  const [pitchPercent, setPitchPercent] = useState(0) // ±8% pitch range
  const [keyLock, setKeyLock] = useState(true) // Key lock enabled by default
  const [adjustedBPM, setAdjustedBPM] = useState(null)

  useImperativeHandle(ref, () => ({
    play: async () => {
      if (!wavesurfer.current) {
        throw new Error('Waveform not loaded')
      }

      const dur = wavesurfer.current.getDuration()
      if (!dur || dur <= 0) {
        throw new Error('Track not ready - duration is 0')
      }

      // Resume AudioContext if suspended
      if (audioContext.state === 'suspended') {
        await audioContext.resume()
      }

      // Seek to cue point (or 0 if no cue set)
      const seekPosition = cuePoint > 0 ? cuePoint / dur : 0
      if (isFinite(seekPosition)) {
        wavesurfer.current.seekTo(seekPosition)
      }

      await wavesurfer.current.play()
      setIsPlaying(true)
    },
    pause: () => {
      if (wavesurfer.current) {
        wavesurfer.current.pause()
        setIsPlaying(false)
      }
    },
    rewindToCue: () => {
      if (wavesurfer.current && duration > 0 && cuePoint > 0) {
        const seekPosition = cuePoint / duration
        if (isFinite(seekPosition)) {
          wavesurfer.current.seekTo(seekPosition)
        }
      }
    },
    getCuePoint: () => cuePoint,
    getEQSettings: () => eq,
    getGainNode: () => gainNode.current // For crossfader control
  }))

  useEffect(() => {
    if (!deckData || !waveformRef.current) return

    loadTrack()

    return () => {
      if (wavesurfer.current) {
        try {
          wavesurfer.current.destroy()
        } catch (error) {
          // Suppress AbortError when cleaning up during track swap
          // This is expected when switching tracks before previous one finishes loading
          if (error.name !== 'AbortError') {
            console.error(`[DECK ${deckLabel}] Cleanup error:`, error)
          }
        }
      }
    }
  }, [deckData])

  const loadTrack = async () => {
    setIsLoading(true)

    try {
      // Initialize WaveSurfer
      if (wavesurfer.current) {
        wavesurfer.current.destroy()
      }

      // Initialize Regions plugin for cue markers
      regionsPlugin.current = Regions.create()

      wavesurfer.current = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: '#333',
        progressColor: '#00ff88',
        cursorColor: '#00ff88',
        barWidth: 2,
        barGap: 1,
        height: 100,
        normalize: true,
        backend: 'WebAudio',
        audioContext: audioContext,
        minPxPerSec: 50, // Default zoom level
        autoScroll: true, // Scroll waveform to keep cursor in viewport
        autoCenter: true, // Cursor STAYS CENTERED - waveform scrolls underneath (DJ mode!)
        hideScrollbar: true,
        plugins: [
          // Minimap: click to jump to position (navigation)
          Minimap.create({
            height: 30,
            waveColor: '#555',
            progressColor: '#00ff88',
            cursorColor: '#00ff88',
            insertPosition: 'beforebegin' // Insert above main waveform
          }),
          // Regions plugin for cue point markers
          regionsPlugin.current
        ]
      })

      // Set up event listeners BEFORE loading to avoid race condition
      wavesurfer.current.on('ready', () => {
        const dur = wavesurfer.current.getDuration()
        setDuration(dur)
        setIsLoading(false)
        console.log(`[DECK ${deckLabel}] Waveform ready, duration: ${dur.toFixed(2)}s`)

        // Initialize Web Audio API filters for EQ
        try {
          const mediaElement = wavesurfer.current.getMediaElement()

          // Create MediaElementSourceNode (only if not already created)
          if (!mediaSource.current) {
            mediaSource.current = audioContext.createMediaElementSource(mediaElement)
          }

          // Create 3-band EQ filters
          lowFilter.current = audioContext.createBiquadFilter()
          lowFilter.current.type = 'lowshelf'
          lowFilter.current.frequency.value = 80 // Low frequency cutoff
          lowFilter.current.gain.value = 0 // Start at 0 dB (neutral)

          midFilter.current = audioContext.createBiquadFilter()
          midFilter.current.type = 'peaking'
          midFilter.current.frequency.value = 1000 // Mid frequency center
          midFilter.current.Q.value = 1.0 // Bandwidth
          midFilter.current.gain.value = 0 // Start at 0 dB (neutral)

          highFilter.current = audioContext.createBiquadFilter()
          highFilter.current.type = 'highshelf'
          highFilter.current.frequency.value = 8000 // High frequency cutoff
          highFilter.current.gain.value = 0 // Start at 0 dB (neutral)

          // Create gain node for crossfader control
          gainNode.current = audioContext.createGain()
          gainNode.current.gain.value = 1.0 // Full volume initially

          // Connect audio chain: source → low → mid → high → gain → destination
          mediaSource.current.connect(lowFilter.current)
          lowFilter.current.connect(midFilter.current)
          midFilter.current.connect(highFilter.current)
          highFilter.current.connect(gainNode.current)
          gainNode.current.connect(audioContext.destination)

          console.log(`[DECK ${deckLabel}] EQ filters initialized (Low: ${lowFilter.current.frequency.value}Hz, Mid: ${midFilter.current.frequency.value}Hz, High: ${highFilter.current.frequency.value}Hz)`)
        } catch (error) {
          console.error(`[DECK ${deckLabel}] Failed to initialize EQ filters:`, error)
        }
      })

      // Throttle audioprocess to avoid excessive re-renders
      let lastUpdate = 0
      wavesurfer.current.on('audioprocess', () => {
        const now = Date.now()
        if (now - lastUpdate > 100) { // Update max 10 times per second
          setCurrentTime(wavesurfer.current.getCurrentTime())
          lastUpdate = now
        }
      })

      wavesurfer.current.on('seek', () => {
        setCurrentTime(wavesurfer.current.getCurrentTime())
      })

      wavesurfer.current.on('finish', () => {
        setIsPlaying(false)
      })

      // Use Railway backend to download full audio (not HLS playlist)
      const downloadUrl = `${RAILWAY_API_URL}/api/download-audio/${deckData.track.id}`
      console.log(`[DECK ${deckLabel}] Loading track from Railway:`, downloadUrl)

      // Load audio AFTER event listeners are attached
      await wavesurfer.current.load(downloadUrl)

    } catch (error) {
      setIsLoading(false)

      // Suppress AbortError - this is expected when swapping tracks quickly
      if (error.name === 'AbortError') {
        console.log(`[DECK ${deckLabel}] Track load aborted (user swapped tracks)`)
        return
      }

      // Only show alert for real errors
      console.error('Failed to load track:', error)
      alert('Failed to load track. Please try again.')
    }
  }

  const handlePlayPause = async () => {
    if (!wavesurfer.current || !wavesurfer.current.getDuration()) return

    try {
      if (isPlaying) {
        // Pause
        wavesurfer.current.pause()
        setIsPlaying(false)
      } else {
        // Play
        // Resume AudioContext if suspended
        if (audioContext.state === 'suspended') {
          await audioContext.resume()
        }

        await wavesurfer.current.play()
        setIsPlaying(true)
      }
    } catch (error) {
      console.error(`[DECK ${deckLabel}] Play/Pause error:`, error)
      alert('Failed to play track. Please try again.')
    }
  }

  const handleStop = () => {
    if (wavesurfer.current) {
      wavesurfer.current.stop()
      setIsPlaying(false)
    }
  }

  const handleSetCue = () => {
    if (wavesurfer.current && regionsPlugin.current && duration > 0) {
      const time = wavesurfer.current.getCurrentTime()

      // Clear existing cue marker region
      regionsPlugin.current.clearRegions()

      // Add new cue marker as a region (region with only start time = marker)
      regionsPlugin.current.addRegion({
        start: time,
        content: 'CUE',
        color: 'rgba(255, 68, 68, 0.3)', // Red with transparency
        drag: false,
        resize: false
      })

      setCuePoint(time)
      console.log(`[DECK ${deckLabel}] Cue point set at ${time.toFixed(2)}s`)
    }
  }

  const handleGoToCue = () => {
    if (wavesurfer.current && duration > 0 && cuePoint > 0) {
      wavesurfer.current.seekTo(cuePoint / duration)
      console.log(`[DECK ${deckLabel}] Jumped to cue point at ${cuePoint.toFixed(2)}s`)
    }
  }

  // Click-and-drag scrubbing with vinyl-style audio preview
  const handleWaveformMouseDown = async (e) => {
    if (!wavesurfer.current || !duration) return

    // Remember if track was playing before scrubbing started
    wasPlayingBeforeScrub.current = isPlaying

    setIsScrubbing(true)

    // Resume audio context for scrubbing
    if (audioContext.state === 'suspended') {
      await audioContext.resume()
    }

    handleWaveformScrub(e)
  }

  const handleWaveformMouseMove = (e) => {
    if (!isScrubbing || !wavesurfer.current || !duration) return
    handleWaveformScrub(e)
  }

  const handleWaveformMouseUp = async () => {
    if (!wavesurfer.current) return

    setIsScrubbing(false)

    // Restore playing state from before scrubbing
    if (wasPlayingBeforeScrub.current) {
      // Was playing before, resume playing
      await wavesurfer.current.play()
      setIsPlaying(true)
    } else {
      // Was paused before, stay paused
      wavesurfer.current.pause()
      setIsPlaying(false)
    }
  }

  const handleWaveformScrub = (e) => {
    if (!wavesurfer.current || !duration) return

    const boundingRect = waveformRef.current.getBoundingClientRect()
    const x = e.clientX - boundingRect.left
    const progress = Math.max(0, Math.min(1, x / boundingRect.width))

    // Seek to position
    wavesurfer.current.seekTo(progress)

    // Vinyl-style scrubbing: play audio continuously while dragging
    if (isScrubbing && !wavesurfer.current.isPlaying()) {
      wavesurfer.current.play()
    }
  }

  const handleZoomIn = () => {
    if (!wavesurfer.current) return
    const newZoom = Math.min(zoomLevel + 50, 500) // Max 500 pixels per second
    setZoomLevel(newZoom)
    wavesurfer.current.zoom(newZoom)
    console.log(`[DECK ${deckLabel}] Zoom in: ${newZoom} px/sec`)
  }

  const handleZoomOut = () => {
    if (!wavesurfer.current) return
    const newZoom = Math.max(zoomLevel - 50, 10) // Min 10 pixels per second
    setZoomLevel(newZoom)
    wavesurfer.current.zoom(newZoom)
    console.log(`[DECK ${deckLabel}] Zoom out: ${newZoom} px/sec`)
  }

  const handleZoomReset = () => {
    if (!wavesurfer.current) return
    const defaultZoom = 50 // Reset to default
    setZoomLevel(defaultZoom)
    wavesurfer.current.zoom(defaultZoom)
    console.log(`[DECK ${deckLabel}] Zoom reset: ${defaultZoom} px/sec`)
  }

  const handleNudgeLeft = () => {
    if (!wavesurfer.current || !duration) return
    const currentPos = wavesurfer.current.getCurrentTime() // Get fresh position
    const newTime = Math.max(currentPos - 0.01, 0) // Move back 10ms
    const seekRatio = newTime / duration
    if (isFinite(seekRatio)) {
      wavesurfer.current.seekTo(seekRatio)
      setCurrentTime(newTime) // Update state immediately
    }
  }

  const handleNudgeRight = () => {
    if (!wavesurfer.current || !duration) return
    const currentPos = wavesurfer.current.getCurrentTime() // Get fresh position
    const newTime = Math.min(currentPos + 0.01, duration) // Move forward 10ms
    const seekRatio = newTime / duration
    if (isFinite(seekRatio)) {
      wavesurfer.current.seekTo(seekRatio)
      setCurrentTime(newTime) // Update state immediately
    }
  }

  const handlePitchChange = (e) => {
    const newPitch = parseFloat(e.target.value)
    setPitchPercent(newPitch)

    // Calculate playback rate (0% = 1.0, +8% = 1.08, -8% = 0.92)
    const playbackRate = 1.0 + (newPitch / 100)

    if (wavesurfer.current) {
      wavesurfer.current.setPlaybackRate(playbackRate)
    }

    // Calculate adjusted BPM if we have original analysis
    if (deckData?.analysis?.bpm) {
      const newBPM = Math.round(deckData.analysis.bpm * playbackRate)
      setAdjustedBPM(newBPM)
      console.log(`[DECK ${deckLabel}] Pitch: ${newPitch > 0 ? '+' : ''}${newPitch}% | BPM: ${deckData.analysis.bpm} → ${newBPM}`)
    }
  }

  const handleKeyLockToggle = () => {
    const newKeyLock = !keyLock
    setKeyLock(newKeyLock)

    // Key lock just visual indicator for now
    // Both pitch and tempo change together (standard playbackRate)
    // TODO: Integrate SoundTouchJS for true independent tempo control
    console.log(`[DECK ${deckLabel}] Key Lock: ${newKeyLock ? 'ON' : 'OFF'} (visual only - full implementation requires SoundTouchJS)`)
  }

  const handlePitchReset = () => {
    setPitchPercent(0)
    setAdjustedBPM(null)
    if (wavesurfer.current) {
      wavesurfer.current.setPlaybackRate(1.0)
    }
    console.log(`[DECK ${deckLabel}] Pitch reset to 0%`)
  }

  // Keyboard controls for fine-tuning
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!wavesurfer.current || !duration) return

      // Only handle arrow keys when this deck is focused
      if (e.target.closest('.deck') !== e.currentTarget?.closest('.deck')) return

      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        handleNudgeLeft()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        handleNudgeRight()
      }
    }

    const deckElement = waveformRef.current?.closest('.deck')
    if (deckElement) {
      deckElement.setAttribute('tabindex', '0') // Make deck focusable
      deckElement.addEventListener('keydown', handleKeyPress)
      return () => deckElement.removeEventListener('keydown', handleKeyPress)
    }
  }, [currentTime, duration])

  // Global mouse event listeners for scrubbing (so it works even when mouse leaves waveform)
  useEffect(() => {
    if (isScrubbing) {
      const handleMouseMove = (e) => handleWaveformMouseMove(e)
      const handleMouseUp = () => handleWaveformMouseUp()

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isScrubbing])

  // Apply EQ changes to Web Audio API filters
  useEffect(() => {
    if (!lowFilter.current || !midFilter.current || !highFilter.current) return

    // Convert slider value (0-100) to gain in dB (-12 to +12)
    // Formula: slider 0 = -12dB, slider 50 = 0dB, slider 100 = +12dB
    const lowGain = (eq.low - 50) * 0.24 // 0.24 = 12/50
    const midGain = (eq.mid - 50) * 0.24
    const highGain = (eq.high - 50) * 0.24

    lowFilter.current.gain.value = lowGain
    midFilter.current.gain.value = midGain
    highFilter.current.gain.value = highGain

    console.log(`[DECK ${deckLabel}] EQ applied: Low ${lowGain.toFixed(1)}dB, Mid ${midGain.toFixed(1)}dB, High ${highGain.toFixed(1)}dB`)
  }, [eq, deckLabel])

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const formatPreciseTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = (seconds % 60).toFixed(3)
    return `${mins}:${secs.padStart(6, '0')}`
  }

  if (!deckData) {
    return (
      <div className="deck empty">
        <div className="empty-deck">
          <p>No track loaded</p>
        </div>
      </div>
    )
  }

  return (
    <div className="deck">
      <div className="deck-track-info">
        <div className="track-artwork-small">
          <img
            src={deckData.track.artwork_url || '/placeholder-artwork.png'}
            alt={deckData.track.title}
          />
        </div>
        <div className="track-details">
          <h4>{deckData.track.title}</h4>
          <p>{deckData.track.user?.username || deckData.track.artist}</p>
          {deckData.analysis && (
            <div className="track-stats-small">
              <span>{adjustedBPM || deckData.analysis.bpm} BPM</span>
              {adjustedBPM && <span className="original-bpm">({deckData.analysis.bpm})</span>}
              <span>{deckData.analysis.key}</span>
            </div>
          )}
        </div>
      </div>

      <div className="pitch-controls">
        <div className="pitch-fader-container">
          <div className="pitch-label">
            <span>PITCH: {pitchPercent > 0 ? '+' : ''}{pitchPercent.toFixed(1)}%</span>
            {deckData.analysis && (
              <div className={`bpm-display ${adjustedBPM ? 'adjusted' : ''}`}>
                {adjustedBPM && <span className="bpm-original">{deckData.analysis.bpm}</span>}
                {adjustedBPM || deckData.analysis.bpm} BPM
              </div>
            )}
          </div>
          <input
            type="range"
            min="-8"
            max="8"
            step="0.1"
            value={pitchPercent}
            onChange={handlePitchChange}
            className="pitch-fader"
            disabled={isLoading || !duration}
          />
          <div className="pitch-marks">
            <span>-8%</span>
            <span>0</span>
            <span>+8%</span>
          </div>
        </div>

        <div className="pitch-buttons">
          <button
            className={`key-lock-btn ${keyLock ? 'active' : ''}`}
            onClick={handleKeyLockToggle}
            disabled={isLoading || !duration}
            title="Key Lock: Preserve pitch when changing tempo"
          >
            {keyLock ? '🔒 Key Lock' : '🔓 Key Off'}
          </button>
          <button
            className="pitch-reset-btn"
            onClick={handlePitchReset}
            disabled={isLoading || !duration || pitchPercent === 0}
            title="Reset pitch to 0%"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="waveform-container">
        {isLoading && (
          <div className="waveform-loading">
            <p>Loading waveform...</p>
          </div>
        )}
        <div
          ref={waveformRef}
          className="waveform"
        />
        {/* Cue marker now rendered by WaveSurfer Regions plugin */}
      </div>

      <div className="zoom-scrub-controls">
        <div className="zoom-controls">
          <button
            className="zoom-btn"
            onClick={handleZoomOut}
            disabled={isLoading || !duration || zoomLevel <= 10}
            title="Zoom out"
          >
            🔍−
          </button>
          <button
            className="zoom-reset-btn"
            onClick={handleZoomReset}
            disabled={isLoading || !duration || zoomLevel === 50}
            title="Reset zoom to default"
          >
            ⟲
          </button>
          <button
            className="zoom-btn"
            onClick={handleZoomIn}
            disabled={isLoading || !duration || zoomLevel >= 500}
            title="Zoom in"
          >
            🔍+
          </button>
          <span className="zoom-level">{zoomLevel}px/s</span>
        </div>

        <div className="scrub-controls">
          <button
            className="scrub-btn"
            onClick={handleNudgeLeft}
            disabled={isLoading || !duration}
            title="Nudge left (or use ← arrow key)"
          >
            ◀ 10ms
          </button>
          <span className="precise-time">{formatPreciseTime(currentTime)}</span>
          <button
            className="scrub-btn"
            onClick={handleNudgeRight}
            disabled={isLoading || !duration}
            title="Nudge right (or use → arrow key)"
          >
            10ms ▶
          </button>
        </div>
      </div>

      <div className="deck-controls">
        <div className="time-display">
          <span className="current-time">{formatTime(currentTime)}</span>
          <span className="separator">/</span>
          <span className="total-time">{formatTime(duration)}</span>
        </div>

        <div className="playback-controls">
          <button
            className={isPlaying ? "pause-btn" : "play-btn"}
            onClick={handlePlayPause}
            disabled={isLoading || !duration}
          >
            {isPlaying ? '⏸ Pause' : '▶ Play'}
          </button>
          <button
            className="stop-btn"
            onClick={handleStop}
            disabled={isLoading || !duration}
          >
            ⏹ Stop
          </button>
        </div>

        <div className="cue-controls">
          <button
            className="set-cue-btn"
            onClick={handleSetCue}
            disabled={isLoading || !duration}
          >
            Set Cue
          </button>
          <button
            className="go-cue-btn"
            onClick={handleGoToCue}
            disabled={isLoading || !duration || cuePoint === 0}
          >
            Go to Cue
          </button>
        </div>
      </div>

      <EQControl
        eq={eq}
        onChange={setEq}
        deckLabel={deckLabel}
      />
    </div>
  )
})

Deck.displayName = 'Deck'

export default Deck
